import { useState } from 'react';
import { Users, Loader2, UserPlus, KeyRound, AlertTriangle } from 'lucide-react';
import { toast } from '@/lib/toast';
import { useAuth } from '@/contexts/AuthContext';
import { useUsers, useCreateUser, useSetUserPassword, useUpdateUserRole } from '@/hooks/useUsers';
import type { AppUser } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

const ROLES = ['viewer', 'editor', 'admin'] as const;
const MIN_PASSWORD_LENGTH = 6;

/** Compact relative time, e.g. "3h ago" — falls back to a date past 30 days. */
function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.round((Date.now() - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
      {children}
    </label>
  );
}

// ── Add user dialog ────────────────────────────────────────────
function AddUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createUser = useCreateUser();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('viewer');

  const reset = () => {
    setEmail('');
    setFullName('');
    setPassword('');
    setRole('viewer');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createUser.mutate(
      {
        email: email.trim(),
        password,
        full_name: fullName.trim() || undefined,
        role,
      },
      {
        onSuccess: () => {
          toast.success(`User ${email.trim()} created`);
          reset();
          onOpenChange(false);
        },
        onError: (err) =>
          toast.error(
            `Could not create user: ${err instanceof Error ? err.message : String(err)}`,
          ),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!createUser.isPending) {
          if (!next) reset();
          onOpenChange(next);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Creates a login with the password you set — the account is active
            right away, no email confirmation needed.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <FieldLabel>Email</FieldLabel>
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="person@dancingoaks.com"
            />
          </div>
          <div>
            <FieldLabel>Full name (optional)</FieldLabel>
            <Input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <FieldLabel>Temporary password</FieldLabel>
            <Input
              type="text"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Share this with the user — they can keep it or you can reset it later.
            </p>
          </div>
          <div>
            <FieldLabel>Role</FieldLabel>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r} className="capitalize">
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createUser.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              )}
              Create user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Reset password dialog ──────────────────────────────────────
function ResetPasswordDialog({
  user,
  onClose,
}: {
  user: AppUser | null;
  onClose: () => void;
}) {
  const setPassword = useSetUserPassword();
  const [value, setValue] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setPassword.mutate(
      { userId: user.id, password: value },
      {
        onSuccess: () => {
          toast.success(`Password updated for ${user.email}`);
          setValue('');
          onClose();
        },
        onError: (err) =>
          toast.error(
            `Could not update password: ${err instanceof Error ? err.message : String(err)}`,
          ),
      },
    );
  };

  return (
    <Dialog
      open={user !== null}
      onOpenChange={(next) => {
        if (!next && !setPassword.isPending) {
          setValue('');
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            Set a new password for{' '}
            <span className="font-medium text-foreground">{user?.email}</span>.
            This takes effect immediately — the user will need the new password
            to sign in.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <FieldLabel>New password</FieldLabel>
            <Input
              type="text"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
              autoFocus
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={setPassword.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={setPassword.isPending}>
              {setPassword.isPending && (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              )}
              Update password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Per-row role selector ──────────────────────────────────────
function RoleSelect({ user }: { user: AppUser }) {
  const updateRole = useUpdateUserRole();

  return (
    <Select
      value={user.role}
      disabled={updateRole.isPending}
      onValueChange={(role) => {
        if (role === user.role) return;
        updateRole.mutate(
          { userId: user.id, role },
          {
            onSuccess: () => toast.success(`${user.email} is now ${role}`),
            onError: (err) =>
              toast.error(
                `Could not update role: ${err instanceof Error ? err.message : String(err)}`,
              ),
          },
        );
      }}
    >
      <SelectTrigger className="h-8 w-28 capitalize">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ROLES.map((r) => (
          <SelectItem key={r} value={r} className="capitalize">
            {r}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Card ───────────────────────────────────────────────────────
export function UserManagementCard() {
  const { user: currentUser } = useAuth();
  const { data: users, isLoading, isError, error, refetch, isFetching } = useUsers();
  const [addOpen, setAddOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<AppUser | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="h-4 w-4" />
          User Management
        </CardTitle>
        <CardDescription>
          Everyone with a login to this app. Add accounts, reset passwords, and
          see when each person last signed in.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-between items-center gap-2">
            <p className="text-xs text-muted-foreground">
              {users ? `${users.length} user${users.length === 1 ? '' : 's'}` : ' '}
            </p>
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <UserPlus className="h-3.5 w-3.5 mr-1" />
              Add user
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading users…
            </div>
          ) : isError ? (
            <div className="space-y-2 py-2">
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                Could not load users
              </div>
              <p className="text-sm text-muted-foreground">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
                disabled={isFetching}
              >
                Try again
              </Button>
            </div>
          ) : (
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead>Last activity</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(users ?? []).map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground">{u.email}</span>
                          {currentUser?.id === u.id && (
                            <Badge variant="secondary" className="text-[10px]">
                              you
                            </Badge>
                          )}
                        </div>
                        {u.full_name && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {u.full_name}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <RoleSelect user={u} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {u.last_sign_in_at
                          ? relativeTime(u.last_sign_in_at)
                          : 'Never'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {relativeTime(u.last_activity_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResetTarget(u)}
                        >
                          <KeyRound className="h-3.5 w-3.5 mr-1" />
                          Reset password
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(users ?? []).length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-sm text-muted-foreground text-center py-6"
                      >
                        No users yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>

      <AddUserDialog open={addOpen} onOpenChange={setAddOpen} />
      <ResetPasswordDialog
        user={resetTarget}
        onClose={() => setResetTarget(null)}
      />
    </Card>
  );
}
